/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable jsx-a11y/no-noninteractive-element-interactions */
import React, { useCallback, useRef, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import _isEmpty from 'lodash/isEmpty';
import OrderSearchDuck from 'ducks/order-search-duck';
import cx from 'classnames';
import { useIsOverflowHorizontal } from 'hooks/useIsOverflow';
import arrowMoveRightSVG from 'public/images/arrow-move-right.svg';
import arrowMoveLeftSVG from 'public/images/arrow-move-left.svg';

import styles from './order-id-list.less';

const OrderIDList = () => {
  const dispatch = useDispatch();
  const tabContainerRef = useRef();
  const orderIDs = useSelector(OrderSearchDuck.selectors.orderIDs);
  const orderMapping = useSelector(OrderSearchDuck.selectors.orderIDMapping);
  const selectedOrderID = useSelector(
    OrderSearchDuck.selectors.selectedOrderID,
  );

  const isOverflow = useIsOverflowHorizontal(tabContainerRef);

  const onSelectOrderID = useCallback(
    orderID => () => {
      if (orderID) {
        dispatch(
          OrderSearchDuck.creators.setSelectedOrder({
            orderID,
            orderGUID: orderMapping[orderID],
          }),
        );
      }
    },
    [orderMapping],
  );

  const renderOrderID = useCallback(
    (orderID, selected) => (
      <div
        key={orderID}
        onClick={onSelectOrderID(orderID)}
        className={cx(styles.orderIDTab, { [styles.selected]: selected })}
        aria-hidden="true"
      >
        {orderID}
      </div>
    ),
    [onSelectOrderID],
  );

  const scrollToSelectedOrder = useCallback(
    () => {
      const listNodes = tabContainerRef.current;
      if (!listNodes) {
        return;
      }
      const selectedNode = listNodes.querySelector(`.${styles.selected}`);
      if (!selectedNode) {
        return;
      }
      selectedNode.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'center',
      });
    },
    [tabContainerRef.current],
  );

  const selectedOrderIDIndex = useMemo(
    () => {
      if (_isEmpty(orderIDs) || !selectedOrderID) {
        return -1;
      }
      return orderIDs.indexOf(selectedOrderID);
    },
    [selectedOrderID, orderIDs],
  );

  const onSelectNextOrder = useCallback(
    index => () => {
      onSelectOrderID(orderIDs[index])();
      scrollToSelectedOrder();
    },
    [onSelectOrderID, orderIDs, scrollToSelectedOrder],
  );

  const renderMoveLeftButton = useMemo(
    () => {
      if (!isOverflow || selectedOrderIDIndex <= 0) {
        return null;
      }
      return (
        <img
          src={arrowMoveLeftSVG}
          className={styles.moveArrow}
          onClick={onSelectNextOrder(selectedOrderIDIndex - 1)}
          alt="move-left"
        />
      );
    },
    [selectedOrderIDIndex, isOverflow, tabContainerRef.current],
  );

  const renderMoveRightButton = useMemo(
    () => {
      if (!isOverflow || selectedOrderIDIndex === orderIDs.length - 1) {
        return null;
      }
      return (
        <img
          src={arrowMoveRightSVG}
          className={styles.moveArrow}
          onClick={onSelectNextOrder(selectedOrderIDIndex + 1)}
          alt="move-right"
        />
      );
    },
    [selectedOrderIDIndex, orderIDs, isOverflow],
  );

  if (_isEmpty(orderIDs) || orderIDs.length === 1) {
    return null;
  }

  return (
    <div className={styles.container}>
      {renderMoveLeftButton}
      <div className={styles.tabContainer} ref={tabContainerRef}>
        {orderIDs.map(orderID =>
          renderOrderID(orderID, orderID === selectedOrderID),
        )}
      </div>
      {renderMoveRightButton}
    </div>
  );
};

export default OrderIDList;
